import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Dialog } from '../../../../components/Dialog';

import { ActorListsChangeVerdict } from './ActorListsChangeVerdict';
import type { PendingChange } from './hooks/useFreezeConfirmedMutations.hook';

interface Props {
  readonly onClose: (isVisible: boolean) => void;
  readonly pendingChange: PendingChange | undefined;
}

// The levels inheriting the lists a change reaches: each keeps its former lists of its own, or
// refuses the change and tells why. The change is confirmed once none refuses it, the verdicts
// asked again after fixing a level in another tab.
export const ActorListsChangeDialog = ({
  onClose,
  pendingChange,
}: Readonly<Props>) => {
  const { folderId = '' } = useParams();
  const [isRechecking, setIsRechecking] = useState(false);
  const verdicts = pendingChange?.freezes ?? [];
  const isRefused = verdicts.some(({ refusal }) => !!refusal);

  const recheck = useCallback(() => {
    setIsRechecking(true);
    void pendingChange?.recheck().finally(() => {
      setIsRechecking(false);
    });
  }, [pendingChange]);

  return (
    <Dialog
      confirmLabel="Confirmer"
      content={
        <Stack spacing={2}>
          <Typography variant="body2">
            Ce changement atteint les niveaux qui héritent de ces listes :
          </Typography>
          {verdicts.map((verdict) => (
            <ActorListsChangeVerdict
              folderId={folderId}
              key={verdict.level}
              verdict={verdict}
            />
          ))}
          {isRefused ? (
            <div>
              <Button
                loading={isRechecking}
                onClick={recheck}
                size="small"
                variant="outlined"
              >
                Revérifier
              </Button>
            </div>
          ) : null}
        </Stack>
      }
      id="actor-lists-freeze"
      isConfirmDisabled={isRefused}
      isVisible={!!pendingChange}
      onCancel={pendingChange?.cancel}
      onConfirm={pendingChange?.proceed}
      setIsVisible={onClose}
      title="Figer les listes des niveaux qui en héritent"
    />
  );
};
